import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Music, Upload, BarChart3, Settings, LogOut, DollarSign, ChevronRight } from 'lucide-react';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/auth');
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Sidebar */}
      <div className="fixed left-0 top-0 bottom-0 w-64 bg-gray-900/50 border-r border-purple-500/20">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-12">
            <div className="bg-purple-500/20 p-2 rounded-xl">
              <img
                src="/Dis-removebg-preview.png"
                alt="Distrotopia Logo"
                className="h-8 w-8"
              />
            </div>
            <span className="text-xl font-bold text-white">
              Distrotopia
            </span>
          </div>

          <nav className="space-y-2">
            <NavItem icon={<Music />} label="Releases" href="/dashboard" />
            <NavItem icon={<Upload />} label="Upload" href="/dashboard/upload" />
            <NavItem icon={<BarChart3 />} label="Analytics" href="/dashboard/analytics" />
            <NavItem icon={<DollarSign />} label="Royalties" href="/dashboard/royalties" />
            <NavItem icon={<Settings />} label="Settings" href="/dashboard/settings" />
          </nav>
        </div>

        <button
          onClick={handleSignOut}
          className="absolute bottom-6 left-6 right-6 flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white hover:bg-purple-500/20 rounded-xl transition-all group"
        >
          <LogOut className="h-5 w-5" />
          <span className="font-medium">Sign Out</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="ml-64 p-8">
        {children}
      </div>
    </div>
  );
};

const NavItem = ({ icon, label, href }: { icon: React.ReactNode; label: string; href: string }) => {
  const isActive = window.location.pathname === href;

  return (
    <a
      href={href}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
        isActive
          ? 'bg-purple-500/20 text-purple-400'
          : 'text-gray-400 hover:text-white hover:bg-purple-500/10'
      } group`}
    >
      {React.cloneElement(icon as React.ReactElement, {
        className: `h-5 w-5 transition-transform group-hover:scale-110 ${
          isActive ? 'text-purple-400' : ''
        }`
      })}
      <span>{label}</span>
      <ChevronRight className={`h-4 w-4 ml-auto transition-transform ${
        isActive ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
      } group-hover:opacity-100 group-hover:translate-x-0`} />
    </a>
  );
};

export default DashboardLayout;